import { useEffect, useState } from "react";
import { apiGet, apiDelete } from "@/lib/api";
import api from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

const STATUS_OPTIONS = ["occupied", "vacant", "maintenance"];
const HOUSE_NUMBERS = Array.from({ length: 44 }, (_, i) => String(i + 1));
const STREETS = ["JALAN 1/1","JALAN 1/2","JALAN 1/3","JALAN 1/4","JALAN 1/5","JALAN 1/6","JALAN 1/7","JALAN 1/8"];

export default function Units() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStreet, setSelectedStreet] = useState("All");
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ unit_number: "", street_number: "", floor: "", status: "vacant" });
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState(null); // unit object
  const [deleteAdminPw, setDeleteAdminPw] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchUnits(); }, []);

  async function fetchUnits() {
    setLoading(true);
    try {
      const res = await apiGet("/units");
      setUnits(res.data);
    } catch (e) {}
    setLoading(false);
  }

  async function changeStatus(id, status) {
    try {
      await api(`/units/${id}`, { method: "PUT", data: { status } });
      setUnits(prev => prev.map(u => u.id === id ? { ...u, status } : u));
    } catch (e) {}
  }

  async function deleteUnit(e) {
    e.preventDefault();
    setDeleteError(""); setDeleting(true);
    try {
      await apiDelete(`/units/${deleteModal.id}`, { admin_password: deleteAdminPw });
      setUnits(prev => prev.filter(u => u.id !== deleteModal.id));
      setDeleteModal(null); setDeleteAdminPw("");
    } catch (err) {
      setDeleteError(err.response?.data?.detail || "Delete failed");
    } finally { setDeleting(false); }
  }

  async function addUnit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api("/units", { method: "POST", data: addForm });
      setShowAdd(false);
      setAddForm({ unit_number: "", street_number: "", floor: "", status: "vacant" });
      await fetchUnits();
    } catch (e) {} finally { setSaving(false); }
  }

  const streets = ["All", ...Array.from(new Set(units.map(u => u.street_number).filter(Boolean))).sort()];
  const filtered = selectedStreet === "All" ? units : units.filter(u => u.street_number === selectedStreet);

  const total    = units.length;
  const occupied = units.filter(u => u.status === "occupied").length;
  const vacant   = units.filter(u => u.status === "vacant").length;
  const maint    = units.filter(u => u.status === "maintenance").length;

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }} data-testid="units-summary">
        {[
          { label: "Total Houses",  val: total,    color: "var(--text)" },
          { label: "Occupied",     val: occupied, color: "var(--brand)" },
          { label: "Vacant",       val: vacant,   color: "var(--text2)" },
          { label: "Maintenance",  val: maint,    color: "var(--pending)" },
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="card-label">{s.label}</div>
            <div className="card-value" style={{ color: s.color }}>{s.val}</div>
            {s.label === "Occupied" && total > 0 && (
              <div style={{ marginTop: 6, height: 4, background: "var(--bg2)", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${(occupied/total)*100}%`, background: "var(--brand-mid)", borderRadius: 2 }} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="section-panel" data-testid="units-panel">
        <div className="section-head">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="section-title">House Overview</div>
            <div style={{ display: "flex", gap: 4 }}>
              {streets.map(s => (
                <button key={s} className={`btn ${selectedStreet === s ? "btn-primary" : ""}`} style={{ padding: "3px 8px", fontSize: 10 }} onClick={() => setSelectedStreet(s)} data-testid={`street-filter-${s}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid="add-unit-btn">
            <Plus size={13} /> Add House
          </button>
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 16, padding: "8px 18px", borderBottom: "1px solid var(--border)", fontSize: 10 }}>
          {[["occupied","var(--brand)","Occupied"], ["vacant","var(--text2)","Vacant"], ["maintenance","var(--pending)","Maintenance"]].map(([s, c, l]) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
              <span style={{ color: "var(--text2)" }}>{l}</span>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text2)", fontSize: 12 }}>Loading houses...</div>
        ) : (
          <div className="unit-grid">
            {filtered.map(u => (
              <div key={u.id} className={`unit-card ${u.status}`} data-testid={`unit-${u.unit_number}`} style={{ position: "relative" }}>
                <div style={{ flex: 1 }} onClick={() => {
                  const next = STATUS_OPTIONS[(STATUS_OPTIONS.indexOf(u.status) + 1) % STATUS_OPTIONS.length];
                  changeStatus(u.id, next);
                }}>
                  <div className="unit-num">{u.unit_number}</div>
                  <div className="unit-status">{capitalize(u.status)}</div>
                </div>
                <button title="Delete house" onClick={e => { e.stopPropagation(); setDeleteModal(u); setDeleteAdminPw(""); setDeleteError(""); }}
                  style={{ position: "absolute", top: 4, right: 4, background: "none", border: "none", cursor: "pointer", opacity: 0.4, padding: 2 }}>
                  <Trash2 size={11} color="var(--danger)" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <div className="modal-overlay">
          <div className="modal-box">
            <div className="modal-title">Add New House</div>
            <form onSubmit={addUnit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">House Number *</label>
                  <select className="form-input" value={addForm.unit_number} onChange={e => setAddForm(f => ({...f, unit_number: e.target.value}))} required data-testid="add-unit-number-input">
                    <option value="">— Select —</option>
                    {HOUSE_NUMBERS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Street</label>
                  <select className="form-input" value={addForm.street_number} onChange={e => setAddForm(f => ({...f, street_number: e.target.value}))} data-testid="add-unit-street-input">
                    <option value="">— Select —</option>
                    {STREETS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-input" value={addForm.status} onChange={e => setAddForm(f => ({...f, status: e.target.value}))} data-testid="add-unit-status-select">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{capitalize(s)}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => setShowAdd(false)} data-testid="cancel-add-unit-btn">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving} data-testid="save-add-unit-btn">{saving ? "Adding..." : "Add House"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete House Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 400 }}>
            <div className="modal-title" style={{ color: "var(--danger)" }}>
              <Trash2 size={14} style={{ marginRight: 6 }} />Delete House
            </div>
            <div style={{ fontSize: 13, color: "var(--text2)", marginBottom: 14 }}>
              Permanently delete house <b>{deleteModal.unit_number}</b> ({deleteModal.street_number})?
              {deleteModal.status === "occupied" && <span style={{ color: "var(--danger)", display: "block", marginTop: 6 }}>⚠ This house is occupied and cannot be deleted.</span>}
            </div>
            {deleteError && <div className="auth-error" style={{ marginBottom: 10 }}>{deleteError}</div>}
            <form onSubmit={deleteUnit}>
              <div className="form-group">
                <label className="form-label">Your Admin Password <span style={{ color: "var(--danger)" }}>*</span></label>
                <input type="password" className="form-input" value={deleteAdminPw} onChange={e => setDeleteAdminPw(e.target.value)} placeholder="Confirm with your admin password" required autoFocus />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn" onClick={() => setDeleteModal(null)}>Cancel</button>
                <button type="submit" className="btn" disabled={deleting || deleteModal.status === "occupied"}
                  style={{ background: "var(--danger)", color: "#fff" }}>
                  {deleting ? "Deleting..." : "Delete House"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function capitalize(s) {
  if (!s) return "";
  return s === "maintenance" ? "Maint." : s.charAt(0).toUpperCase() + s.slice(1);
}
